/**
 * Cyber (RB §5.6).
 *
 * Diff this file against RB §5.6.1–5.6.5 row by row.
 *
 * The one structural point: **the base TN is the target's hardening**
 * (`CYBER_BASE_TN` in `model/world.ts`), and every action is an *offset* on that
 * base. Reconnoiter is base −3; Gain Access is base against the router; Escalate
 * Privilege is base +1 to USER and +3 to ADMIN; Deploy Payload and Establish
 * Persistence are base +1. The defensive actions have flat TNs of their own,
 * because they are not rolled against a hardening the defender is attacking.
 *
 * That has a consequence worth stating where somebody will read it: **the attacker
 * does not know the TN they are rolling against** until Reconnoiter succeeds. So
 * the M5 order builder cannot show a modifier preview for a cyber action, and a
 * `RollRecord` for one must never reach a player's intel report — it would hand
 * over the hardening for free and make Reconnoiter pointless (§4.14 of the M3 plan).
 */
function action(a) {
    return Object.freeze(a);
}
const ACTION_LIST = Object.freeze([
    action({
        id: 'RECONNOITER',
        tnOffset: -3,
        flatTn: null,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: true,
        summary: "Reveals the target's hardening rating.",
    }),
    action({
        id: 'GAIN_ACCESS',
        tnOffset: 0,
        flatTn: null,
        requiresAccess: 'NO_ACCESS',
        againstRouter: true,
        offensive: true,
        summary: 'NO ACCESS → FOOTHOLD on that network. Exceptional: also USER on one asset. Complication: detected and attributed.',
    }),
    action({
        id: 'ESCALATE_PRIVILEGE',
        // +1 to USER, +3 to ADMIN — the offset depends on the rung being climbed, so
        // the resolver reads `ESCALATE_TN_OFFSET` rather than this field.
        tnOffset: 1,
        flatTn: null,
        requiresAccess: 'FOOTHOLD',
        againstRouter: false,
        offensive: true,
        summary: 'Climbs one rung on one asset. Complication: detected and attributed.',
    }),
    action({
        id: 'DEPLOY_PAYLOAD',
        tnOffset: 1,
        flatTn: null,
        requiresAccess: 'USER',
        againstRouter: false,
        offensive: true,
        summary: 'Delivers a payload at the access level it requires.',
    }),
    action({
        id: 'ESTABLISH_PERSISTENCE',
        tnOffset: 1,
        flatTn: null,
        requiresAccess: 'USER',
        againstRouter: false,
        offensive: true,
        summary: "The defender's Hunt against this asset takes [-4]. If access is lost, re-enter at FOOTHOLD once free.",
    }),
    // -------------------------------------------------------------------------
    // Defensive actions (RB §5.6.5)
    // -------------------------------------------------------------------------
    action({
        id: 'HUNT',
        tnOffset: null,
        flatTn: 14,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: false,
        summary: 'Reveals attacker access on one asset and removes one rung. Exceptional: removes all and identifies the attacking network. Complication: reports "no compromise detected" regardless.',
    }),
    action({
        id: 'HARDEN',
        tnOffset: null,
        flatTn: null,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: false,
        summary: 'VULNERABLE → NORMAL, or NORMAL → HARDENED. Reveals the rating to the defender.',
    }),
    action({
        id: 'MONITOR',
        tnOffset: null,
        flatTn: null,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: false,
        summary: 'HARDENED → MONITORED.',
    }),
    action({
        id: 'ISOLATE',
        tnOffset: null,
        flatTn: null,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: false,
        summary: 'Blocks lateral movement to and from the asset and all payloads against it. While isolated it receives no orders.',
    }),
    action({
        id: 'RESTORE',
        tnOffset: null,
        flatTn: 11,
        requiresAccess: 'NO_ACCESS',
        againstRouter: false,
        offensive: false,
        summary: 'Halves the remaining recovery of one cyber-caused DEGRADED or OFFLINE effect.',
    }),
]);
export const CYBER_ACTIONS = Object.freeze(Object.fromEntries(ACTION_LIST.map((a) => [a.id, a])));
export const CYBER_MISSION_IDS = Object.freeze(ACTION_LIST.map((a) => a.id));
export function cyberActionDef(id) {
    return CYBER_ACTIONS[id];
}
export function isCyberMission(id) {
    return CYBER_ACTIONS[id] !== undefined;
}
/** RB §5.6.3: "base +1 to USER, base +3 to ADMIN". */
export const ESCALATE_TN_OFFSET = Object.freeze({
    USER: 1,
    ADMIN: 3,
});
/**
 * Lateral movement (RB §5.6.3): `[+2]` with USER on another asset of the same
 * network, `[+4]` with ADMIN.
 *
 * Computed **at the moment of the roll** from current access, which is why cyber
 * resolves sequentially (decision M3-D2): an Exceptional Gain Access earlier in
 * this phase enables the bonus later in the same phase.
 */
export const LATERAL_MOVEMENT_BONUS = Object.freeze({
    USER: 2,
    ADMIN: 4,
});
/** RB §5.6.5: Hunt takes [-4] against Persistence, [+4] if the asset is MONITORED. */
export const HUNT_PERSISTENCE_PENALTY = -4;
export const HUNT_MONITORED_BONUS = 4;
/**
 * Restore halves the remaining recovery, rounding **down** (decision M3-D18).
 *
 * RB §5.6.5 does not say how to round. `floor` makes a 6-turn Wiper a 3-turn one
 * and clears a 1-turn effect; `ceil` would leave a 1-turn effect untouched, which
 * reads as a bug rather than as a rule.
 */
export function restoredExpiry(currentTurn, expiresAtTurn) {
    const remaining = Math.max(0, expiresAtTurn - currentTurn);
    return currentTurn + Math.floor(remaining / 2);
}
function payload(p) {
    return Object.freeze(p);
}
const PAYLOAD_LIST = Object.freeze([
    payload({
        id: 'COLLECT',
        requiresAccess: 'USER',
        loud: false,
        severity: null,
        durationTurns: null,
        escalation: 1,
        summary: 'The target becomes CHARACTERIZED and FIXED, and its queued mission this turn is revealed.',
    }),
    payload({
        id: 'DENY',
        requiresAccess: 'USER',
        loud: false,
        severity: 'DEGRADED',
        durationTurns: 2,
        escalation: 2,
        summary: 'Target DEGRADED for 2 turns.',
    }),
    payload({
        id: 'CORRUPT',
        requiresAccess: 'ADMIN',
        loud: false,
        severity: 'DEGRADED',
        durationTurns: 4,
        escalation: 3,
        summary: 'Target DEGRADED for 4 turns.',
    }),
    payload({
        id: 'HIJACK',
        requiresAccess: 'ADMIN',
        loud: true,
        severity: null,
        durationTurns: null,
        escalation: 5,
        summary: "Issue one order to the target this turn, overriding the owner's.",
    }),
    payload({
        id: 'WIPER',
        requiresAccess: 'ADMIN',
        loud: true,
        severity: 'OFFLINE',
        durationTurns: 6,
        escalation: 6,
        summary: 'Target OFFLINE for 6 turns.',
    }),
]);
export const PAYLOADS = Object.freeze(Object.fromEntries(PAYLOAD_LIST.map((p) => [p.id, p])));
export function payloadDef(id) {
    return PAYLOADS[id];
}
/** RB §5.6.1: MONITORED detects every attacker action against it automatically. */
export function autoDetects(hardening) {
    return hardening === 'MONITORED';
}
/** RB §5.6.5, Harden and Monitor: one step up the ladder, or `null` at the top. */
export const HARDENING_LADDER = Object.freeze({
    VULNERABLE: 'NORMAL',
    NORMAL: 'HARDENED',
    HARDENED: 'MONITORED',
    MONITORED: null,
});
export const CYBER_TABLE_MANIFEST = {
    CYBER_ACTIONS,
    PAYLOADS,
    ESCALATE_TN_OFFSET,
    LATERAL_MOVEMENT_BONUS,
    HUNT_PERSISTENCE_PENALTY,
    HUNT_MONITORED_BONUS,
    HARDENING_LADDER,
};
//# sourceMappingURL=cyber.js.map